import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, enBase64, ErreurApi, lire, requeteMoi } from '../api'
import { Icone } from './Icone'

// Dictée de l'indication d'une pièce ou d'une tâche : enregistrée dans le navigateur,
// transcrite par l'API (Deepgram), le texte s'ajoute au champ et reste modifiable avant l'envoi.

/** Une minute au plus : au-delà, l'enregistrement s'arrête seul. */
export const DUREE_MAX_MS = 60_000

export function dicteePossible(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  )
}

type Etat = 'repos' | 'ecoute' | 'transcription'

/**
 * Enregistrer puis transcrire : `commencer` ouvre le micro (à appeler dans le geste de
 * l'humain), `arreter` envoie l'audio, `annuler` le jette.
 */
export function useDictee({
  onTexte,
  pour = 'indication',
  repli = 'tapez l’indication',
}: {
  onTexte: (texte: string) => void
  /** Une tâche garde un texte plus long qu'une indication. */
  pour?: 'indication' | 'tache'
  /** Ce qu'il reste à faire quand la dictée échoue. */
  repli?: string
}) {
  const [etat, setEtat] = useState<Etat>('repos')
  const [message, setMessage] = useState<string | null>(null)
  const enregistreur = useRef<MediaRecorder | null>(null)
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null)
  const jeter = useRef(false)
  // Le rappel le plus récent : le texte s'ajoute à ce qui est saisi quand il revient.
  const rappel = useRef(onTexte)
  useEffect(() => {
    rappel.current = onTexte
  })

  // Quitter la page coupe le micro.
  useEffect(
    () => () => {
      if (minuterie.current) clearTimeout(minuterie.current)
      jeter.current = true
      enregistreur.current?.stream.getTracks().forEach((t) => t.stop())
    },
    [],
  )

  async function transcrire(audio: Blob, typeMime: string) {
    setEtat('transcription')
    try {
      const r = await lire(
        api.pieces.transcrire.$post({
          json: { audioBase64: enBase64(await audio.arrayBuffer()), typeMime, pour },
        }),
      )
      if (r.texte) rappel.current(r.texte)
      else setMessage(`Rien entendu : réessayez, ou ${repli}.`)
    } catch (e) {
      setMessage(e instanceof ErreurApi ? e.message : `Dictée impossible : ${repli}.`)
    } finally {
      setEtat('repos')
    }
  }

  async function commencer() {
    setMessage(null)
    let flux: MediaStream
    try {
      flux = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setMessage(`Micro refusé : autorisez-le dans le navigateur, ou ${repli}.`)
      return
    }
    const rec = new MediaRecorder(flux)
    const morceaux: Blob[] = []
    jeter.current = false
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) morceaux.push(e.data)
    }
    rec.onstop = () => {
      flux.getTracks().forEach((t) => t.stop())
      if (minuterie.current) clearTimeout(minuterie.current)
      enregistreur.current = null
      if (jeter.current) {
        setEtat('repos')
        return
      }
      const typeMime = rec.mimeType || morceaux[0]?.type || 'audio/webm'
      void transcrire(new Blob(morceaux, { type: typeMime }), typeMime)
    }
    enregistreur.current = rec
    rec.start()
    setEtat('ecoute')
    minuterie.current = setTimeout(() => rec.state !== 'inactive' && rec.stop(), DUREE_MAX_MS)
  }

  function arreter() {
    const rec = enregistreur.current
    if (rec && rec.state !== 'inactive') rec.stop()
  }

  function annuler() {
    jeter.current = true
    arreter()
  }

  return { etat, message, commencer, arreter, annuler }
}

export function BoutonDictee({
  onTexte,
  pour,
}: {
  onTexte: (texte: string) => void
  pour?: 'indication' | 'tache'
}) {
  const { etat, message, commencer, arreter } = useDictee({
    onTexte,
    ...(pour === 'tache' && { pour, repli: 'tapez le texte' }),
  })

  return (
    <>
      <button
        type="button"
        className={etat === 'ecoute' ? 'bouton bouton-ecoute' : 'bouton-discret'}
        onClick={etat === 'ecoute' ? arreter : () => void commencer()}
        disabled={etat === 'transcription'}
        aria-pressed={etat === 'ecoute'}
      >
        <Icone nom={etat === 'ecoute' ? 'stop_circle' : 'mic'} />
        {etat === 'ecoute' ? 'Arrêter' : etat === 'transcription' ? 'Transcription…' : 'Dicter'}
      </button>
      {message && <span className="discret">{message}</span>}
    </>
  )
}

/**
 * Indication facultative du déposant (« perso », « Flow Design, déjeuner client ») : écrite,
 * ou dictée quand la clé Deepgram est configurée et que le navigateur sait enregistrer.
 */
export function ChampIndication({
  id,
  valeur,
  onChange,
}: {
  id: string
  valeur: string
  onChange: (texte: string) => void
}) {
  const moi = useQuery(requeteMoi).data
  return (
    <div className="champ champ-indication">
      <label htmlFor={id}>Indication (facultative)</label>
      <textarea
        id={id}
        rows={2}
        maxLength={500}
        placeholder="Où va la pièce : « perso », « Flow Design, déjeuner client »…"
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="dictee">
        {moi?.dictee && dicteePossible() && (
          <BoutonDictee
            onTexte={(texte) => onChange(valeur.trim() ? `${valeur.trim()} ${texte}` : texte)}
          />
        )}
        <span className="aide">
          Lue avec la pièce : elle l’emporte pour l’entité, sauf si la pièce en nomme une autre.
        </span>
      </div>
    </div>
  )
}
